<template>
  <v-autocomplete
    :model-value="modelValue"
    :items="sortedItems"
    :item-title="itemTitle"
    :custom-filter="filterAnywhere"
    :name="uniqueName"
    autocomplete="off"
    @update:model-value="$emit('update:modelValue', $event)"
  />
</template>

<script setup>
// Drop-in replacement for v-select / v-autocomplete, used for every dropdown
// in the app. Differences from a plain v-select:
//   1. Always searchable, and the search matches anywhere in the item's
//      text (not just from the start) - e.g. typing "blue" finds
//      "PETG Blue Sky Sunlu".
//   2. Chrome ignores autocomplete="off" on its own for its "previously
//      typed into a same-name field" suggestions dropdown (as opposed to
//      saved-password/address autofill, which it does respect) - it keys
//      that suggestion cache off the input's `name`. Vuetify doesn't set
//      one, so every unnamed autocomplete on the page shared one bucket and
//      Chrome would surface whatever was last typed into any of them.
//      Giving each instance its own random name (never repeated, so Chrome
//      never has history for it) plus autocomplete="off" reliably stops it.
//   3. Items are sorted alphabetically by their displayed title (locale-
//      aware, current UI language) by default - every entity lookup in the
//      app (components, warehouses, stations, users, ...) is otherwise in
//      whatever order the API returned, usually insertion/id order, which
//      isn't a useful default for a picker. Pass preserve-order for the
//      handful of dropdowns whose order IS the point (a workflow's status
//      list, a skill ladder) - sorting those alphabetically would scramble
//      a meaningful order.
// Every other v-select/v-autocomplete prop (item-value, rules, clearable,
// multiple, chips, density, style, ...) passes straight through via Vue's
// automatic attribute fallthrough - only modelValue, items and item-title
// are intercepted here (the first for v-model, the latter two to sort).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: [String, Number, Boolean, Array, Object],
    default: null
  },
  items: {
    type: Array,
    default: () => []
  },
  itemTitle: {
    type: [String, Function],
    default: 'title'
  },
  preserveOrder: {
    type: Boolean,
    default: false
  }
});

defineEmits(['update:modelValue']);

const { locale } = useI18n();
const uniqueName = `ss-${crypto.randomUUID()}`;

// vue-i18n's locale values ('UA'/'EN') aren't valid Intl tags on their own
// (UA is a country, not a language) - map to the actual language for
// correct Cyrillic-aware collation.
const collatorLocale = computed(() => (locale.value === 'UA' ? 'uk' : 'en'));

function titleOf(item) {
  if (typeof props.itemTitle === 'function') return props.itemTitle(item);
  if (item != null && typeof item === 'object') return item[props.itemTitle] ?? '';
  return item ?? '';
}

const sortedItems = computed(() => {
  if (props.preserveOrder) return props.items;
  const collator = new Intl.Collator(collatorLocale.value, { sensitivity: 'base', numeric: true });
  return [...props.items].sort((a, b) => collator.compare(String(titleOf(a)), String(titleOf(b))));
});

function filterAnywhere(value, query, item) {
  if (!query) return true;
  const text = String(item?.title ?? value ?? '').toLocaleLowerCase();
  return text.includes(query.toLocaleLowerCase());
}
</script>
